import React, { useState } from "react";
import { Visibility } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tooltip,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  Feedback,
  FEEDBACK_TYPES,
  FEEDBACK_STATUS_LABELS,
} from "../../../../types/feedback.types";
import { formatIsoToBrDateTimeShort } from "../../../../utils/functions";
import { Colors } from "../../../../constants/Colors";

interface Props {
  feedback: Feedback;
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.5, sm: 2 },
      py: 1,
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: Colors.commons.darkGray, minWidth: { sm: 140 } }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, wordBreak: "break-word" }}>{children}</Box>
  </Box>
);

const Value: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2">
    {children === null || children === undefined || children === "" ? "—" : children}
  </Typography>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{ mt: 2, mb: 0.5, color: Colors.primary[100], fontWeight: 700 }}
  >
    {children}
  </Typography>
);

const labelType = (type?: string) =>
  FEEDBACK_TYPES.find((option) => option.value === type)?.label ?? type ?? "—";

/** Lê do mapa cru só quando o campo plano equivalente não veio. */
const fromMap = (map: Record<string, unknown> | undefined, key: string) => {
  const value = map?.[key];
  return value === null || value === undefined ? undefined : String(value);
};

const ViewFeedbackDetails: React.FC<Props> = ({ feedback }) => {
  const [open, setOpen] = useState(false);

  const { app, device } = feedback;

  const appVersion = feedback.appVersion ?? fromMap(app, "version");
  const buildNumber = feedback.buildNumber ?? fromMap(app, "buildNumber");
  const environment = fromMap(app, "environment");
  const isDevice = app?.isDevice;

  const platform = feedback.platform ?? fromMap(device, "osName");
  const osVersion = feedback.osVersion ?? fromMap(device, "osVersion");
  const deviceModel =
    feedback.deviceModel ??
    [fromMap(device, "brand") ?? fromMap(device, "manufacturer"), fromMap(device, "model")]
      .filter(Boolean)
      .join(" ");

  const osLabel = [platform, osVersion].filter(Boolean).join(" ");

  const hasContext = Boolean(
    appVersion || buildNumber || environment || deviceModel || osLabel || feedback.deviceInfo
  );

  return (
    <>
      <Tooltip title="Ver mais" placement="top">
        <Visibility
          sx={{
            color: Colors.primary[100],
            cursor: "pointer",
            ":hover": { opacity: "70%" },
          }}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do feedback</DialogTitle>
        <DialogContent dividers>
          <DetailRow label="Título">
            <Value>{feedback.subject ?? feedback.title}</Value>
          </DetailRow>
          <DetailRow label="Categoria">
            <Chip size="small" label={labelType(feedback.type)} />
          </DetailRow>
          <DetailRow label="Data">
            <Value>
              {feedback.createdAt ? formatIsoToBrDateTimeShort(feedback.createdAt) : null}
            </Value>
          </DetailRow>
          <DetailRow label="Status">
            <Value>
              {feedback.status ? FEEDBACK_STATUS_LABELS[feedback.status] ?? feedback.status : null}
            </Value>
          </DetailRow>
          <DetailRow label="Usuário">
            <Value>{feedback.userName ?? feedback.userEmail}</Value>
          </DetailRow>
          <DetailRow label="ID do usuário">
            <Value>{feedback.userId}</Value>
          </DetailRow>

          <Divider />
          <SectionTitle>Origem no app</SectionTitle>
          {/* Tela e módulo ficaram de fora: o feedback sai sempre do perfil. */}
          <DetailRow label="Sessão">
            {/* Cruza este feedback com os crashes da mesma execução do app. */}
            <Value>{feedback.sessionId}</Value>
          </DetailRow>
          <DetailRow label="Trace">
            {/* Elo com o log do backend no Kibana. */}
            <Value>{feedback.traceId}</Value>
          </DetailRow>

          <Divider />
          <SectionTitle>Aplicativo e aparelho</SectionTitle>
          {hasContext ? (
            <>
              <DetailRow label="Versão do app">
                <Value>
                  {appVersion ? `${appVersion}${buildNumber ? ` (${buildNumber})` : ""}` : null}
                </Value>
              </DetailRow>
              <DetailRow label="Ambiente">
                <Value>{environment}</Value>
              </DetailRow>
              <DetailRow label="Aparelho físico">
                <Value>{isDevice === undefined ? null : isDevice ? "Sim" : "Não"}</Value>
              </DetailRow>
              <DetailRow label="Modelo">
                <Value>{deviceModel}</Value>
              </DetailRow>
              <DetailRow label="Sistema">
                <Value>{osLabel}</Value>
              </DetailRow>
              <DetailRow label="Tipo">
                <Value>{fromMap(device, "deviceType")}</Value>
              </DetailRow>
              <DetailRow label="Idioma">
                <Value>{fromMap(device, "locale")}</Value>
              </DetailRow>
              <DetailRow label="ID do aparelho">
                <Value>{fromMap(device, "deviceId")}</Value>
              </DetailRow>
              {feedback.deviceInfo && (
                <DetailRow label="Info do aparelho">
                  <Value>{feedback.deviceInfo}</Value>
                </DetailRow>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: Colors.commons.darkGray, py: 1 }}>
              Sem informações de dispositivo para este feedback.
            </Typography>
          )}

          <Divider />
          <SectionTitle>Descrição</SectionTitle>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", py: 1 }}>
            {feedback.description || "—"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewFeedbackDetails;
